import { useId } from "react";
import styles from "./Field.module.css";

interface FieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  /**
   * When set, error text replaces helper (never stacks). Border also
   * turns `--danger`. Per §9: "no red until submit is attempted" —
   * callers should hold off setting `error` until user has submitted
   * or blurred.
   */
  error?: string;
  helper?: string;
  /** When set, current length + max length renders bottom-right. */
  counter?: number;
  disabled?: boolean;
  type?: "text" | "tel" | "email" | "password" | "search" | "number" | "date";
  autoComplete?: string;
  placeholder?: string;
  inputMode?: "text" | "numeric" | "tel" | "email" | "search";
  maxLength?: number;
  /** Optional field ID; auto-generated if not provided. */
  id?: string;
}

/**
 * Field — labeled text input with a single-line footer that carries
 * exactly one of {helper, error, blank}. Counter (when present) sits
 * to the right and is exclusive of the helper/error text.
 */
export function Field({
  label,
  value,
  onChange,
  error,
  helper,
  counter,
  disabled = false,
  type = "text",
  autoComplete,
  placeholder,
  inputMode,
  maxLength,
  id,
}: FieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const describedById = error ? `${fieldId}-error` : helper ? `${fieldId}-helper` : undefined;
  const overLimit = counter !== undefined && value.length > counter;

  return (
    <div className={styles.field}>
      <label htmlFor={fieldId} className={styles.label}>
        {label}
      </label>
      <input
        id={fieldId}
        className={`${styles.input} ${error ? styles.error : ""}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        aria-invalid={!!error}
        aria-describedby={describedById}
      />
      <div className={styles.footer}>
        {error ? (
          <span id={`${fieldId}-error`} className={styles.errorText} role="alert">
            {error}
          </span>
        ) : helper ? (
          <span id={`${fieldId}-helper`} className={styles.helper}>
            {helper}
          </span>
        ) : (
          <span aria-hidden />
        )}
        {counter !== undefined ? (
          <span
            className={`${styles.counter} ${overLimit ? styles.counterOver : ""}`}
          >
            {value.length}/{counter}
          </span>
        ) : null}
      </div>
    </div>
  );
}
